import { useEffect, useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { qty as fq } from "./stockUtils";

const TYPES = [["receipt", "Receive"], ["issue", "Issue"], ["transfer", "Transfer"], ["adjust", "Stock count"], ["return", "Return"]];
const HELP = {
  receipt: "Book goods into a store or van (ad-hoc — use a purchase order for supplier deliveries).",
  issue: "Take parts out of stock without a job (e.g. consumables, write-offs). Parts used on jobs are issued from the job page.",
  transfer: "Move parts between a store and a van, or van to van.",
  adjust: "Set the counted quantity at a location — the difference is recorded as an adjustment.",
  return: "Put unused parts back into a store or van.",
};

/** Searchable part picker: filter box + select (catalogues can be large). */
export function PartPicker({ parts, value, onChange, testid = "part-picker", locationId }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = parts;
    if (needle) rows = rows.filter((p) => [p.name, p.part_number, p.category, p.supplier_name, p.barcode].join(" ").toLowerCase().includes(needle));
    return rows.slice(0, 200);
  }, [parts, q]);
  const atLoc = (p) => locationId ? (p.by_location || []).find((l) => l.location_id === locationId)?.qty || 0 : p.qty_total;
  return (
    <div className="space-y-1.5">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts by name, number, barcode…" data-testid={`${testid}-search`} />
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={parts.length ? "Choose a part" : "No parts in the catalogue yet"} /></SelectTrigger>
        <SelectContent className="max-h-72">
          {list.map((p) => <SelectItem key={p.id} value={p.id}><span className="font-medium">{p.name}</span>{p.part_number && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{p.part_number}</span>}<span className="ml-1.5 text-[11px] text-muted-foreground">· {fq(atLoc(p))} {p.unit}</span></SelectItem>)}
          {list.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No parts match</div>}
        </SelectContent>
      </Select>
    </div>
  );
}

export function MovementDialog({ open, initialType = "receipt", part, parts = [], locations = [], onClose, onDone }) {
  const [f, setF] = useState({ type: initialType, part_id: "", location_id: "", to_location_id: "", qty: "", unit_cost: "", note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const store = locations.find((l) => l.type === "store") || locations[0];
    setF({ type: initialType, part_id: part?.id || "", location_id: store?.id || "", to_location_id: "", qty: "", unit_cost: part?.unit_cost ? String(part.unit_cost) : "", note: "" });
  }, [open, initialType, part, locations]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const chosen = parts.find((p) => p.id === f.part_id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { type: f.type, part_id: f.part_id, location_id: f.location_id, qty: Number(f.qty), note: f.note };
      if (f.type === "transfer") body.to_location_id = f.to_location_id;
      if (f.type === "receipt" && f.unit_cost !== "") body.unit_cost = Number(f.unit_cost);
      const { data } = await API.post("/stock/movements", body);
      toast.success({ receipt: "Stock received", issue: "Parts issued", transfer: "Stock transferred", adjust: "Stock count saved", return: "Parts returned" }[f.type]);
      onDone?.(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't record the movement"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="movement-dialog">
        <DialogHeader><DialogTitle>Stock movement</DialogTitle><DialogDescription>{HELP[f.type]}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap gap-1.5">{TYPES.map(([v, l]) => <button key={v} type="button" onClick={() => set("type", v)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.type === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`movement-type-${v}`}>{l}</button>)}</div>
          <div><Label>Part</Label><PartPicker parts={parts} value={f.part_id} onChange={(v) => set("part_id", v)} locationId={f.location_id} testid="movement-part" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{f.type === "transfer" ? "From" : "Location"}</Label>
              <Select value={f.location_id} onValueChange={(v) => set("location_id", v)}><SelectTrigger data-testid="movement-location"><SelectValue placeholder="Choose location" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.type === "van" ? ` (van${l.operative_name ? ` · ${l.operative_name}` : ""})` : ""}</SelectItem>)}</SelectContent></Select></div>
            {f.type === "transfer" ? (
              <div><Label>To</Label>
                <Select value={f.to_location_id} onValueChange={(v) => set("to_location_id", v)}><SelectTrigger data-testid="movement-to-location"><SelectValue placeholder="Destination" /></SelectTrigger>
                  <SelectContent>{locations.filter((l) => l.id !== f.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.type === "van" ? " (van)" : ""}</SelectItem>)}</SelectContent></Select></div>
            ) : (
              <div><Label>{f.type === "adjust" ? "Counted quantity" : "Quantity"}{chosen ? ` (${chosen.unit})` : ""}</Label><Input type="number" min="0" step="any" required value={f.qty} onChange={(e) => set("qty", e.target.value)} data-testid="movement-qty" /></div>
            )}
            {f.type === "transfer" && <div><Label>Quantity{chosen ? ` (${chosen.unit})` : ""}</Label><Input type="number" min="0" step="any" required value={f.qty} onChange={(e) => set("qty", e.target.value)} data-testid="movement-qty" /></div>}
            {f.type === "receipt" && <div><Label>Unit cost (£)</Label><Input type="number" min="0" step="0.01" value={f.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} placeholder="Updates the catalogue cost" data-testid="movement-unit-cost" /></div>}
          </div>
          <div><Label>Note{f.type === "issue" ? " *" : ""}</Label><Input value={f.note} onChange={(e) => set("note", e.target.value)} required={f.type === "issue"} placeholder={f.type === "issue" ? "Why these parts were used" : "Optional"} data-testid="movement-note" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving || !f.part_id || !f.location_id} data-testid="movement-save">{saving ? "Saving…" : "Record"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
